import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { huntService, HuntSummary } from '@/services/api/huntService';
import { Button } from '@/components/common/Button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Users, Clock, CheckCircle, Play, Pause, Trash2, AlertTriangle, MapPin, Package, Hexagon } from 'lucide-react';
import { HuntStatus } from '@gps-hunt/shared';
import { useHuntStore } from '@/stores/huntStore';

/**
 * Delete confirmation modal
 */
interface DeleteConfirmModalProps {
  hunt: HuntSummary;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  hunt,
  onConfirm,
  onCancel,
  isDeleting,
}) => {
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl w-full max-w-md p-6 animate-slide-up">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Delete Hunt?</h2>
            <p className="text-sm text-text-secondary">This action cannot be undone</p>
          </div>
        </div>

        <div className="mb-6 p-4 bg-background rounded-lg">
          <div className="font-medium text-text-primary">{hunt.name}</div>
          <div className="text-sm text-text-secondary mt-1">
            Code: {hunt.joinCode} • {hunt.participantCount} participant{hunt.participantCount !== 1 ? 's' : ''}
          </div>
        </div>

        <p className="text-sm text-text-secondary mb-6">
          This will permanently delete the hunt and all associated data including pins, photos, and participant records.
        </p>

        <div className="flex gap-3">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isDeleting}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex-1 bg-red-600 hover:bg-red-700"
          >
            {isDeleting ? 'Deleting...' : 'Delete Hunt'}
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * My Hunts page - lists all hunts created or joined by the current user
 */
export const MyHuntsPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentHunt, reset: resetHuntStore } = useHuntStore();
  const [createdHunts, setCreatedHunts] = useState<HuntSummary[]>([]);
  const [joinedHunts, setJoinedHunts] = useState<HuntSummary[]>([]);
  const [activeTab, setActiveTab] = useState<'created' | 'joined'>('created');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<HuntSummary | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchHunts = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await huntService.getMyHunts();
        setCreatedHunts(response.createdHunts || response.hunts || []);
        setJoinedHunts(response.joinedHunts || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load hunts');
      } finally {
        setIsLoading(false);
      }
    };

    fetchHunts();
  }, []);

  const handleDeleteClick = (hunt: HuntSummary, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteTarget(hunt);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    setIsDeleting(true);
    try {
      await huntService.deleteHunt(deleteTarget.id);
      // Remove from both lists in case it appears in one
      setCreatedHunts(prev => prev.filter(h => h.id !== deleteTarget.id));
      setJoinedHunts(prev => prev.filter(h => h.id !== deleteTarget.id));
      // Clear currentHunt if the deleted hunt was the active one
      if (currentHunt?.id === deleteTarget.id) {
        resetHuntStore();
      }
      setDeleteTarget(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete hunt');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
  };

  const getStatusIcon = (status: HuntStatus) => {
    switch (status) {
      case HuntStatus.LOBBY:
        return <Pause className="w-4 h-4 text-yellow-500" />;
      case HuntStatus.SETUP:
        return <Clock className="w-4 h-4 text-blue-500" />;
      case HuntStatus.READY:
        return <Play className="w-4 h-4 text-green-500" />;
      case HuntStatus.ACTIVE:
        return <Play className="w-4 h-4 text-primary" />;
      case HuntStatus.COMPLETED:
        return <CheckCircle className="w-4 h-4 text-text-secondary" />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status: HuntStatus) => {
    switch (status) {
      case HuntStatus.LOBBY:
        return 'Waiting';
      case HuntStatus.SETUP:
        return 'Setup';
      case HuntStatus.READY:
        return 'Ready';
      case HuntStatus.ACTIVE:
        return 'Active';
      case HuntStatus.COMPLETED:
        return 'Completed';
      default:
        return status;
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleHuntClick = (hunt: HuntSummary) => {
    if (hunt.status === HuntStatus.COMPLETED) {
      navigate(`/stats/${hunt.id}`);
    } else {
      navigate(`/lobby/${hunt.id}`);
    }
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const activeHunts = activeTab === 'created' ? createdHunts : joinedHunts;
  const isCreatedTab = activeTab === 'created';

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Fixed header */}
      <div className="flex-shrink-0 p-6 pb-0">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-text-primary">My Hunts</h1>
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate('/create')}
            >
              Create New
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-surface-light">
            <button
              onClick={() => setActiveTab('created')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'created'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-text-secondary hover:text-text-primary'
              }`}
            >
              Created ({createdHunts.length})
            </button>
            <button
              onClick={() => setActiveTab('joined')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'joined'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-text-secondary hover:text-text-primary'
              }`}
            >
              Joined ({joinedHunts.length})
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-auto px-6 py-4">
        <div className="max-w-2xl mx-auto space-y-4">
          {error && (
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400">{error}</p>
            </div>
          )}

          {activeHunts.length === 0 && !error && (
            <div className="text-center py-12 space-y-4">
              <p className="text-text-secondary">
                {isCreatedTab 
                  ? 'You have not created any hunts yet.' 
                  : 'You have not joined any hunts yet.'}
              </p>
              {isCreatedTab && (
                <Button
                  variant="primary"
                  onClick={() => navigate('/create')}
                >
                  Create Your First Hunt
                </Button>
              )}
            </div>
          )}

          <div className="space-y-3">
            {activeHunts.map((hunt) => (
              <div
                key={hunt.id}
                className="p-4 bg-surface rounded-lg border border-surface-light hover:border-primary/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <button
                    onClick={() => handleHuntClick(hunt)}
                    className="flex-1 text-left min-w-0"
                  >
                    <div className="font-semibold text-text-primary truncate">
                      {hunt.name}
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-sm font-mono text-primary">
                        {hunt.joinCode}
                      </span>
                      <div className="flex items-center gap-1 px-2 py-0.5 bg-surface-light rounded text-xs">
                        {getStatusIcon(hunt.status)}
                        <span className="text-text-secondary">{getStatusLabel(hunt.status)}</span>
                      </div>
                      {!isCreatedTab && hunt.myRole && (
                        <div className="px-2 py-0.5 bg-primary/20 text-primary rounded text-xs capitalize">
                          {hunt.myRole}
                        </div>
                      )}
                    </div>
                    {!isCreatedTab && hunt.creatorDisplayName && (
                      <div className="text-xs text-text-muted mt-1">
                        by {hunt.creatorDisplayName}
                      </div>
                    )}
                  </button>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="flex items-center gap-1 text-text-secondary text-sm">
                      <Users className="w-4 h-4" />
                      <span>{hunt.participantCount}</span>
                    </div>
                    {isCreatedTab && (
                      <button
                        onClick={(e) => handleDeleteClick(hunt, e)}
                        className="p-2 text-text-muted hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                        aria-label="Delete hunt"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                {hunt.description && (
                  <p className="text-sm text-text-secondary mb-2 line-clamp-2">
                    {hunt.description}
                  </p>
                )}
                {/* Hunt stats */}
                <div className="flex items-center gap-4 text-xs text-text-muted mb-2">
                  <div className="flex items-center gap-1" title="Pins">
                    <MapPin className="w-3.5 h-3.5" />
                    <span>{hunt.pinCount ?? 0}</span>
                  </div>
                  <div className="flex items-center gap-1" title="Items">
                    <Package className="w-3.5 h-3.5" />
                    <span>{hunt.itemCount ?? 0}</span>
                  </div>
                  <div className="flex items-center gap-1" title="Challenges">
                    <Hexagon className="w-3.5 h-3.5" />
                    <span>{hunt.challengeCount ?? 0}</span>
                  </div>
                </div>
                <div className="text-xs text-text-muted">
                  Created {formatDate(hunt.createdAt)}
                  {hunt.completedAt && ` • Completed ${formatDate(hunt.completedAt)}`}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sticky footer */}
      <div className="flex-shrink-0 px-4 pt-4 pb-8 border-t border-surface-light safe-bottom">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => navigate('/welcome')}
            className="text-text-secondary hover:text-primary transition-colors text-sm"
          >
            ← Back to Home
          </button>
        </div>
      </div>

      {deleteTarget && (
        <DeleteConfirmModal
          hunt={deleteTarget}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          isDeleting={isDeleting}
        />
      )}
    </div>
  );
};

export default MyHuntsPage;
